/**
 * @typedef {Object} MemorySnapshot
 * @property {number} jsHeapSizeLimit
 * @property {number} totalJSHeapSize
 * @property {number} usedJSHeapSize
 */

/**
 * @typedef {Object} MetricPayload
 * @property {string} id
 * @property {string} name
 * @property {number} value
 * @property {number} delta
 * @property {string} rating
 * @property {Array} [entries]
 * @property {MemorySnapshot} [memory]
 */

let hasLoggedLoadTime = false;

/**
 * Computes the current page load time using the Navigation Timing API.
 *
 * @returns {number | undefined} The load time in milliseconds, if available.
 */
const getLoadTime = () => {
  if (typeof performance === 'undefined') {
    return undefined;
  }

  if (performance.getEntriesByType) {
    const [navigationEntry] = performance.getEntriesByType('navigation');
    if (navigationEntry && navigationEntry.loadEventEnd) {
      return navigationEntry.loadEventEnd - navigationEntry.startTime;
    }
  }

  if (performance.timing) {
    const { loadEventEnd, navigationStart } = performance.timing;
    if (loadEventEnd && navigationStart) {
      return loadEventEnd - navigationStart;
    }
  }

  return undefined;
};

/**
 * Normalises the memory snapshot from the Web Vitals metric or the browser.
 *
 * @param {MemorySnapshot | undefined} memory
 * @returns {MemorySnapshot | undefined}
 */
const getMemorySnapshot = memory => {
  if (memory) {
    return memory;
  }

  if (typeof performance !== 'undefined' && performance.memory) {
    const { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize } = performance.memory;
    return { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize };
  }

  return undefined;
};

/**
 * Sends the metric payload to the console. Replace this function to integrate
 * with an analytics endpoint.
 *
 * @param {MetricPayload & { timestamp: number, loadTime?: number }} payload
 *   The payload to log.
 */
const publishMetric = payload => {
  // eslint-disable-next-line no-console
  console.info('[metrics]', payload);
};

/**
 * Logs Web Vitals metrics alongside load time and memory usage snapshots.
 *
 * @param {MetricPayload} metric The metric emitted by `reportWebVitals`.
 */
const logMetrics = metric => {
  if (!metric || typeof window === 'undefined') {
    return;
  }

  if (!hasLoggedLoadTime) {
    const loadTime = getLoadTime();
    if (typeof loadTime === 'number') {
      publishMetric({
        id: 'page-load',
        name: 'Load Time',
        value: loadTime,
        delta: 0,
        rating: 'info',
        entries: [],
        timestamp: Date.now(),
        loadTime,
      });
    }
    hasLoggedLoadTime = true;
  }

  const memory = getMemorySnapshot(metric.memory);
  const payload = {
    ...metric,
    memory,
    timestamp: Date.now(),
  };

  publishMetric(payload);
};

export default logMetrics;
